import { useEffect } from 'react';

import { useLocation } from 'react-router';

import { getWindowSafe } from '@change-corgi/core/window';

type UseScrollToHashOptions = Readonly<{
	elementId: string;
	targetHash: string;
	enabled?: boolean;
	offset?: number;
	behavior?: ScrollBehavior;
}>;

export function useScrollToHash({
	elementId,
	targetHash,
	enabled = true,
	offset = 0,
	behavior = 'smooth',
}: UseScrollToHashOptions): void {
	const { hash: navHash } = useLocation();
	const currentWindow = getWindowSafe();

	useEffect(() => {
		if (!enabled || !currentWindow) return undefined;

		// Comparing React Router navigation hash
		if (navHash !== targetHash) return undefined;

		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		// Wait for next frame to ensure DOM is ready, then add a small delay for layout to settle
		const animationFrameId = requestAnimationFrame(() => {
			timeoutId = setTimeout(() => {
				const element = currentWindow.document.getElementById(elementId);
				if (element) {
					const y = element.getBoundingClientRect().top + currentWindow.pageYOffset - offset;
					currentWindow.scrollTo({ top: y, behavior });
				}
			}, 100);
		});

		return () => {
			cancelAnimationFrame(animationFrameId);
			if (timeoutId !== undefined) {
				clearTimeout(timeoutId);
			}
		};
	}, [enabled, currentWindow, navHash, targetHash, elementId, offset, behavior]);
}
